import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import MemberNameCell from "@/components/MemberNameCell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useChurch } from "@/hooks/useChurch";
import { usePermissions } from "@/hooks/usePermissions";
import { CalendarCheck, Plus, QrCode, Users, UserPlus, Eye, Trash2, MoreHorizontal, History, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import StatCard from "@/components/StatCard";
import EventFormDialog from "@/components/attendance/EventFormDialog";
import QRCodeDialog from "@/components/attendance/QRCodeDialog";
import { toast } from "sonner";
import { format } from "date-fns";

interface Event {
  id: string;
  name: string;
  event_type: string;
  event_date: string;
  start_time: string | null;
  is_active: boolean;
  notes: string | null;
  created_at: string;
}

interface AttendanceRecord {
  id: string;
  event_id: string;
  member_name: string;
  is_visitor: boolean;
  checkin_time: string;
  status: string;
}

const MINISTRY_NOTE_PREFIX = "Ministry: ";
const MINISTRY_IDS_NOTE_PREFIX = "Ministry IDs: ";
const LEGACY_ORG_NOTE_PREFIX = "Organization: ";
const LEGACY_ORG_IDS_NOTE_PREFIX = "Organization IDs: ";

const getOrganizationFromNotes = (notes: string | null) => {
  if (!notes) return "";
  const firstLine = notes.split("\n")[0]?.trim() || "";
  if (firstLine.startsWith(MINISTRY_NOTE_PREFIX)) return firstLine.replace(MINISTRY_NOTE_PREFIX, "").trim();
  if (firstLine.startsWith(LEGACY_ORG_NOTE_PREFIX)) return firstLine.replace(LEGACY_ORG_NOTE_PREFIX, "").trim();
  return "";
};

const getOrganizationIdsFromNotes = (notes: string | null) => {
  if (!notes) return [] as string[];
  const lines = notes.split("\n").map((line) => line.trim());
  const idsLine = lines.find((line) =>
    line.startsWith(MINISTRY_IDS_NOTE_PREFIX) || line.startsWith(LEGACY_ORG_IDS_NOTE_PREFIX)
  );
  if (!idsLine) return [] as string[];
  return idsLine
    .replace(MINISTRY_IDS_NOTE_PREFIX, "")
    .replace(LEGACY_ORG_IDS_NOTE_PREFIX, "")
    .split(">")
    .map((id) => id.trim())
    .filter(Boolean);
};

type OrganizationNode = {
  id: string;
  name: string;
  parent_id: string | null;
};

const Attendance = () => {
  const navigate = useNavigate();
  const { role } = useAuth();
  const { profile } = useChurch();
  const { getAttendanceOrganizationIds, hasAttendanceScopedAccess } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [qrEvent, setQrEvent] = useState<{ id: string; name: string } | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(searchParams.get("event_id"));
  const [deleteEventId, setDeleteEventId] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const queryClient = useQueryClient();
  const allowedAttendanceOrgIds = getAttendanceOrganizationIds();
  const attendanceScoped = role !== "admin" && hasAttendanceScopedAccess();

  // Sync URL param with state
  useEffect(() => {
    const paramEventId = searchParams.get("event_id");
    if (paramEventId && paramEventId !== selectedEventId) {
      setSelectedEventId(paramEventId);
    }
  }, [searchParams]);

  // Fetch events
  const { data: events = [] } = useQuery({
    queryKey: ["events", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .eq("church_id", profile!.id)
        .order("event_date", { ascending: false });
      if (error) throw error;
      return data as Event[];
    },
    enabled: !!profile?.id,
  });

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations", "attendance_scope"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, parent_id");
      if (error) throw error;
      return (data || []) as OrganizationNode[];
    },
  });

  // Fetch attendance for selected event
  const { data: attendees = [] } = useQuery({
    queryKey: ["attendance", selectedEventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("attendance_records")
        .select("*")
        .eq("event_id", selectedEventId!)
        .order("checkin_time", { ascending: false });
      if (error) throw error;
      return data as AttendanceRecord[];
    },
    enabled: !!selectedEventId,
  });

  // Fetch attendance counts per event for the table
  const { data: eventCounts = {} } = useQuery({
    queryKey: ["event_counts", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("attendance_records")
        .select("event_id, is_visitor")
        .eq("church_id", profile!.id);
      if (error) throw error;
      const counts: Record<string, { members: number; visitors: number }> = {};
      (data || []).forEach((r: any) => {
        if (!counts[r.event_id]) counts[r.event_id] = { members: 0, visitors: 0 };
        if (r.is_visitor) counts[r.event_id].visitors++;
        else counts[r.event_id].members++;
      });
      return counts;
    },
    enabled: !!profile?.id,
  });

  // Realtime subscription for live updates
  useEffect(() => {
    const channel = supabase
      .channel("attendance-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "attendance_records" }, () => {
        queryClient.invalidateQueries({ queryKey: ["attendance"] });
        queryClient.invalidateQueries({ queryKey: ["event_counts"] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [queryClient]);

  const deleteEventMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("events").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["event_counts"] });
      toast.success("Event deleted");
      setDeleteEventId(null);
      if (selectedEventId === deleteEventId) setSelectedEventId(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const scopedOrgSet = useMemo(() => {
    if (!attendanceScoped) return null;

    const byParent = new Map<string, string[]>();
    organizations.forEach((org) => {
      if (!org.parent_id) return;
      const current = byParent.get(org.parent_id) || [];
      current.push(org.id);
      byParent.set(org.parent_id, current);
    });

    const visited = new Set<string>();
    const queue = [...allowedAttendanceOrgIds];
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      const children = byParent.get(currentId) || [];
      children.forEach((childId) => {
        if (!visited.has(childId)) queue.push(childId);
      });
    }

    return visited;
  }, [attendanceScoped, organizations, allowedAttendanceOrgIds]);

  const organizationNameToIds = useMemo(() => {
    const map = new Map<string, string[]>();
    organizations.forEach((org) => {
      const key = org.name.toLowerCase();
      const existing = map.get(key) || [];
      existing.push(org.id);
      map.set(key, existing);
    });
    return map;
  }, [organizations]);

  const scopedEvents = useMemo(() => {
    if (!attendanceScoped || !scopedOrgSet) return events;

    return events.filter((event) => {
      const orgIdsFromNotes = getOrganizationIdsFromNotes(event.notes);
      if (orgIdsFromNotes.length > 0) {
        return orgIdsFromNotes.some((orgId) => scopedOrgSet.has(orgId));
      }

      const orgPath = getOrganizationFromNotes(event.notes);
      if (!orgPath) return false;
      const orgNames = orgPath.split(">").map((part) => part.trim().toLowerCase()).filter(Boolean);
      if (orgNames.length === 0) return false;

      return orgNames.some((name) => {
        const matchedIds = organizationNameToIds.get(name) || [];
        return matchedIds.some((id) => scopedOrgSet.has(id));
      });
    });
  }, [events, attendanceScoped, scopedOrgSet, organizationNameToIds]);

  // Chart data: attendance by recent events
  const totalToday = scopedEvents
    .filter((e) => e.event_date === new Date().toISOString().split("T")[0])
    .reduce((sum, e) => {
      const c = eventCounts[e.id];
      return sum + (c ? c.members + c.visitors : 0);
    }, 0);

  const selectedEvent = scopedEvents.find((e) => e.id === selectedEventId);
  const selectedCounts = selectedEventId ? eventCounts[selectedEventId] : null;
  const memberCount = selectedCounts?.members || 0;
  const visitorCount = selectedCounts?.visitors || 0;

  return (
    <div className="space-y-6">
      {/* Shared tab bar */}
      <div className="flex items-center justify-between">
        <div className="flex h-9 rounded-none bg-muted p-1 gap-1">
          <button
            onClick={() => navigate("/events")}
            className="h-7 px-3 text-xs font-medium rounded-none inline-flex items-center gap-1.5 bg-transparent text-muted-foreground hover:bg-secondary transition-colors"
          >
            <CalendarCheck className="w-3.5 h-3.5" />Upcoming
          </button>
          <button
            onClick={() => navigate("/events?tab=past")}
            className="h-7 px-3 text-xs font-medium rounded-none inline-flex items-center gap-1.5 bg-transparent text-muted-foreground hover:bg-secondary transition-colors"
          >
            <History className="w-3.5 h-3.5" />Past
          </button>
          <button
            onClick={() => navigate("/events?tab=calendar")}
            className="h-7 px-3 text-xs font-medium rounded-none inline-flex items-center gap-1.5 bg-transparent text-muted-foreground hover:bg-secondary transition-colors"
          >
            <CalendarDays className="w-3.5 h-3.5" />Calendar
          </button>
          <button
            className="h-7 px-3 text-xs font-medium rounded-none inline-flex items-center gap-1.5 bg-primary text-primary-foreground"
          >
            <CalendarCheck className="w-3.5 h-3.5" />Attendance
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <StatCard icon={CalendarCheck} label="Total Events" value={String(scopedEvents.length)} />
        <StatCard icon={Users} label="Checked In Today" value={String(totalToday)} />
        <StatCard
          icon={UserPlus}
          label="Selected Event"
          value={selectedCounts ? `${memberCount + visitorCount} (${visitorCount} visitors)` : "—"}
        />
      </div>



      {/* Events list */}
      {scopedEvents.length === 0 ? (
        <div className="stat-card text-center py-12">
          <CalendarCheck className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No events yet. Create your first event to start tracking attendance!</p>
        </div>
      ) : (
        <div className="stat-card p-0 overflow-hidden">
          {(() => {
            const totalPages = Math.max(1, Math.ceil(scopedEvents.length / pageSize));
            const adjPage = Math.min(currentPage, totalPages);
            const pagedEvents = scopedEvents.slice((adjPage - 1) * pageSize, adjPage * pageSize);
            return (
          <>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[500px]">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-6 py-3">Event</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-6 py-3">Date</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-6 py-3">Type</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-6 py-3">Attendance</th>
                <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wider px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {pagedEvents.map((e) => {
                const c = eventCounts[e.id];
                const total = c ? c.members + c.visitors : 0;
                return (
                  <tr key={e.id} className="border-b border-border/50 hover:bg-primary/10 hover:shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.18)] transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-foreground flex items-center gap-2">
                      <CalendarCheck className="w-4 h-4 text-primary" /> {e.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">{e.event_date}</td>
                    <td className="px-6 py-4"><Badge variant="secondary">{e.event_type}</Badge></td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-semibold text-foreground">{total}</span>
                      {c && c.visitors > 0 && (
                        <span className="text-xs text-muted-foreground ml-1">({c.visitors} visitors)</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setQrEvent({ id: e.id, name: e.name })} title="Show QR Code">
                          <QrCode className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/attendance/event/${e.id}`)} title="View Attendees">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1 rounded hover:bg-secondary transition-colors">
                              <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setDeleteEventId(e.id)} className="text-destructive focus:text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" /> Delete Event
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-border/40">
            <p className="text-xs text-muted-foreground">Showing {(adjPage - 1) * pageSize + 1}–{Math.min(adjPage * pageSize, scopedEvents.length)} of {scopedEvents.length}</p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Rows per page:</span>
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
                <SelectTrigger className="h-7 w-20 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              {totalPages > 1 && (
                <>
                  <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={adjPage === 1} onClick={() => setCurrentPage(1)}>«</Button>
                  <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={adjPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>‹</Button>
                  <span className="text-xs px-2">{adjPage} / {totalPages}</span>
                  <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={adjPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>›</Button>
                  <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={adjPage === totalPages} onClick={() => setCurrentPage(totalPages)}>»</Button>
                </>
              )}
            </div>
          </div>
          </>
            );
          })()}
        </div>
      )}

      {/* Attendees panel for selected event */}
      {selectedEvent && (
        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading font-semibold text-foreground">{selectedEvent.name} — Attendees</h3>
              <p className="text-sm text-muted-foreground">
                {memberCount} members · {visitorCount} visitors · {memberCount + visitorCount} total
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSelectedEventId(null)}>Close</Button>
          </div>
          {attendees.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No check-ins yet for this event.</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {attendees.map((a) => (
                <div key={a.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-secondary/20">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${a.is_visitor ? "bg-accent/10 text-accent" : "bg-primary/10 text-primary"}`}>
                      {a.member_name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                    </div>
                    <div>
                      <MemberNameCell name={a.member_name} />
                      {a.is_visitor && <Badge variant="secondary" className="text-xs">Visitor</Badge>}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(a.checkin_time), "h:mm a")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Dialogs */}
      <EventFormDialog open={showCreateEvent} onClose={() => setShowCreateEvent(false)} />
      {qrEvent && (
        <QRCodeDialog
          open={!!qrEvent}
          onClose={() => setQrEvent(null)}
          eventId={qrEvent.id}
          eventName={qrEvent.name}
        />
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteEventId} onOpenChange={() => setDeleteEventId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this event?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the event and all its attendance records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteEventId && deleteEventMutation.mutate(deleteEventId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Attendance;
